import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, MOTION } from "../../theme/tokens";

/**
 * One printout row in the printout list. Every row shares this footprint — chosen or not, editable or
 * not — so picking a printout moves only the wash and the edge, never the text.
 */
export const presetRowSx = (selected: boolean) => (theme: Theme) => ({
	display: "flex",
	alignItems: "center",
	gap: 1,
	width: "100%",
	boxSizing: "border-box" as const,
	minHeight: CONTROL_HEIGHT.COMPACT,
	px: 1,
	border: "1px solid",
	borderColor: selected ? alpha(theme.palette.primary.main, ALPHA.FOCUS_RING) : "transparent",
	borderRadius: 1,
	backgroundColor: selected ? alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) : "transparent",
	transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

/**
 * A printout's name: brand ink and weight on the chosen one, one quiet line otherwise. The chosen weight
 * is the app's heading weight (600, as `buildAppTheme` sets for h1–h6); the theme has no named token for
 * it, unlike `fontWeightMedium`.
 */
export const presetNameSx = (selected: boolean) => (theme: Theme) => ({
	flex: 1,
	minWidth: 0,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap" as const,
	fontSize: FONT_SIZE.EMPHASIS,
	fontWeight: selected ? theme.typography.h6.fontWeight : theme.typography.fontWeightMedium,
	color: selected ? theme.palette.primary.dark : INK.SECONDARY,
});
